// v2 fir — a dense cone to the ground: six overlapping tiers whose wide skirt
// brushes the soil, shadow-dark at the base and brightening upward, capped with a
// spike tip, so the whole tree reads as one full, symmetric green cone with no
// visible trunk. Variants re-nudge the tiers off-axis, re-roll their radii a few
// percent and shift the deep-green palette deterministically. Ported 1:1 from
// age-of-mcp's v2/tree-fir.ts, using the library's shared `tree` (h01, lerp,
// pick, tier) helpers.
import * as THREE from "three";
import { defineModel, tree, type Part } from "../../lib";

const PALETTES = [
  { shadow: "#1d4530", deep: "#245539", mid: "#2c6343", lite: "#357250" },
  { shadow: "#1b432e", deep: "#225236", mid: "#296040", lite: "#326f4d" },
  { shadow: "#1f4731", deep: "#26573a", mid: "#2e6545", lite: "#377452" },
] as const;

const BARK = "#3a2a1b";

// [y, r, h] per tier, ground skirt to crown.
const TIERS: readonly [number, number, number][] = [
  [0.26, 0.6, 0.42],
  [0.52, 0.52, 0.46],
  [0.78, 0.44, 0.46],
  [1.04, 0.36, 0.44],
  [1.3, 0.28, 0.42],
  [1.55, 0.2, 0.4],
];

function firParts(v: number): Part[] {
  const seed = 149 + v * 19;
  const pal = tree.pick(PALETTES, v);
  const parts: Part[] = [
    // a stub of trunk for ground contact, hidden under the skirt
    { geometry: new THREE.CylinderGeometry(0.07, 0.1, 0.12, 5),
      color: BARK, position: [0, 0.06, 0] },
  ];
  for (let i = 0; i < TIERS.length; i++) {
    const [y, r, h] = TIERS[i]!;
    const color = i === 0 ? pal.shadow
      : i >= TIERS.length - 2 ? pal.lite
      : i % 2 === 1 ? pal.deep : pal.mid;
    const jitter = tree.lerp(0.95, 1.05, tree.h01(seed, 5 + i));
    parts.push(tree.tier(seed, 10 + i * 3, y, r * jitter, h, color, 6, 0.018));
  }
  // spike tip
  parts.push(tree.tier(seed, 40, 1.74, 0.09, 0.26, pal.lite, 6, 0.005));
  return parts;
}

export const treeFirV2 = defineModel({
  id: "tree-fir-v2",
  batchId: "fable",
  name: "Tree fir (v2)",
  tags: ["scenery","tree","fir","low-poly","age-of-mcp","fable"],
  summary: "Low-poly tree fir.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.5,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => firParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => firParts(1) },
    { id: "variant-2", label: "Variant 3", parts: () => firParts(2) },
  ],
  animations: [],
});
